import { Suspense, lazy, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
// B3 (Optimierung 2026-08-25): der QR-Code erscheint nur bei der
// 2FA-Einrichtung — die Bibliothek muss nicht in den Haupt-Chunk.
const QRCodeSVG = lazy(() => import('qrcode.react').then((m) => ({ default: m.QRCodeSVG })));
import {
  ApiError,
  getAuthControllerMeQueryKey,
  useAuthControllerDisableTotp,
  useAuthControllerEnableTotp,
  useAuthControllerSetupTotp,
} from '@werkbank/shared';
import { notify } from '../lib/notify';

/**
 * Zwei-Faktor-Anmeldung (TOTP) auf der Profilseite — reine Wiederverwendung
 * der gehärteten Auth-Endpoints (Vollanalyse M4 + Profil-2FA):
 * - Einrichtung/Neu-Einrichtung verlangt IMMER das aktuelle Passwort; das
 *   Secret liegt bis zur Code-Bestätigung nur im Staging (aktive 2FA bleibt
 *   bis dahin unangetastet).
 * - Deaktivierung verlangt Passwort UND gültigen Code.
 * Das Secret wird ausschließlich während der Einrichtung angezeigt und beim
 * Abschluss/Abbruch aus dem State geräumt; der Server liefert es nie wieder.
 */
export function TotpCard({ totpEnabled }: { totpEnabled: boolean }) {
  const queryClient = useQueryClient();

  const [password, setPassword] = useState('');
  const [pending, setPending] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const invalidateMe = () =>
    queryClient.invalidateQueries({ queryKey: getAuthControllerMeQueryKey() });

  const setup = useAuthControllerSetupTotp({
    mutation: {
      onSuccess: (data) => {
        setPending(data);
        setPassword('');
        setCode('');
      },
    },
  });
  const enable = useAuthControllerEnableTotp({
    mutation: {
      onSuccess: () => {
        setPending(null);
        setCode('');
        void invalidateMe();
        notify.success('Zwei-Faktor-Anmeldung ist aktiv.');
      },
    },
  });
  const disable = useAuthControllerDisableTotp({
    mutation: {
      onSuccess: () => {
        setPassword('');
        setDisableCode('');
        setPending(null);
        void invalidateMe();
        notify.success('Zwei-Faktor-Anmeldung wurde deaktiviert.');
      },
    },
  });

  const error =
    (setup.error instanceof ApiError && setup.error.message) ||
    (enable.error instanceof ApiError && enable.error.message) ||
    (disable.error instanceof ApiError && disable.error.message) ||
    null;

  return (
    <Card padding={24}>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600} style={{ fontSize: 'var(--wb-font-size-lg)' }}>
            Zwei-Faktor-Anmeldung
          </Text>
          <Badge color={totpEnabled ? 'teal' : 'gray'} variant="light" data-testid="totp-status">
            {totpEnabled ? 'Aktiv' : 'Nicht aktiv'}
          </Badge>
        </Group>

        {!pending && (
          <>
            <Text size="sm" c="dimmed">
              {totpEnabled
                ? 'Die Anmeldung verlangt zusätzlich einen Einmal-Code aus Ihrer Authenticator-App. Zum Deaktivieren oder für ein neues Gerät ist das Passwort erforderlich.'
                : 'Schützt Ihre Anmeldung zusätzlich mit Einmal-Codes einer Authenticator-App (z. B. Aegis, Google Authenticator).'}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <PasswordInput
                label="Aktuelles Passwort"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="current-password"
                data-testid="totp-password"
              />
              {totpEnabled && (
                <TextInput
                  label="Code aus der App"
                  description="Zum Deaktivieren zusätzlich erforderlich."
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.currentTarget.value)}
                  autoComplete="one-time-code"
                  maxLength={6}
                  data-testid="totp-disable-code"
                />
              )}
            </SimpleGrid>
            {totpEnabled ? (
              <>
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="default"
                    loading={setup.isPending}
                    disabled={password.length === 0}
                    onClick={() => setup.mutate({ data: { currentPassword: password } })}
                    data-testid="totp-rotate"
                  >
                    Neu einrichten (neues Gerät)
                  </Button>
                  <Button
                    color="red"
                    loading={disable.isPending}
                    disabled={password.length === 0 || disableCode.length < 6}
                    onClick={() =>
                      disable.mutate({ data: { currentPassword: password, code: disableCode } })
                    }
                    data-testid="totp-disable"
                  >
                    Deaktivieren
                  </Button>
                </Group>
              </>
            ) : (
              <Group justify="flex-end">
                <Button
                  loading={setup.isPending}
                  disabled={password.length === 0}
                  onClick={() => setup.mutate({ data: { currentPassword: password } })}
                  data-testid="totp-setup"
                >
                  Einrichtung starten
                </Button>
              </Group>
            )}
          </>
        )}

        {pending && (
          <>
            <Alert variant="light" color="blue">
              QR-Code mit der Authenticator-App scannen und zur Bestätigung den angezeigten Code
              eingeben.{' '}
              {totpEnabled && 'Die bisherige Einrichtung bleibt bis zur Bestätigung gültig.'}
            </Alert>
            {/* Weißes Feld über den Mantine-Token (Hardcode-Gate: keine
                Hexfarben außerhalb der Theme-Schicht) — bewusst NICHT
                theme-abhängig, damit der QR auch im Dunkelmodus scanbar bleibt. */}
            <Group justify="center">
              <div
                style={{
                  background: 'var(--mantine-color-white)',
                  padding: 12,
                  borderRadius: 8,
                }}
              >
                <Suspense fallback={<div style={{ width: 180, height: 180 }} />}>
                  <QRCodeSVG value={pending.otpauthUrl} size={180} />
                </Suspense>
              </div>
            </Group>
            <Text size="sm" c="dimmed" ta="center">
              Manuell: <Code data-testid="totp-secret">{pending.secret}</Code>
            </Text>
            <Group justify="center" align="flex-end" gap="sm">
              <TextInput
                label="Code aus der App"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                autoComplete="one-time-code"
                maxLength={6}
                data-testid="totp-enable-code"
              />
              <Button
                loading={enable.isPending}
                disabled={code.length < 6}
                onClick={() => enable.mutate({ data: { code } })}
                data-testid="totp-enable"
              >
                Aktivieren
              </Button>
              <Button variant="default" onClick={() => setPending(null)} data-testid="totp-cancel">
                Abbrechen
              </Button>
            </Group>
          </>
        )}

        {error && (
          <Text c="red" size="sm" data-testid="totp-error">
            {error}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
